import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import {
    BorderRadiusMedium,
    ColorNeutralBlack,
    ColorNeutralWhite,
    ColorNordicGrey3,
    Spacing16
} from 'autogen/design-tokens/tokens';
import type { IProductVideo } from 'autogen/swagger/all';
import { HowTo } from 'autogen/translation-keys/trans-website-product-videos';
import Card from 'components/generic/card';
import Tag from 'components/generic/tag';
import { Typography } from 'components/generic/typography';
import useVideoDetails, { type VideoDetailsProps } from 'components/video/hooks/useVideoDetails';
import dynamic from 'helpers/dynamic';
import ReactLoadingSpinner from 'modules/loadingSpinner';
import { GA4Tracking } from 'modules/tracking/GA4';

/* #region  styles */
const ButtonContainer = styled.button<{ $loading?: boolean }>`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 155px;
    margin: 0;
    padding: 0;
    background: 0;
    border: 0;
    border-radius: ${BorderRadiusMedium};
    
    &:focus, &:focus-visible {
        outline: 2px auto ${ColorNeutralBlack};
        outline-offset: -1px;
    }

    ${({ $loading }) =>
        $loading &&
        css`
            cursor: default;
            background-color: ${ColorNordicGrey3};
    `};
`;

const StyledImg = styled.img`
    object-fit: cover;
    object-position: center;
    width: 100%;
    height: 155px;
`;

const StyledLoading = styled(ReactLoadingSpinner)`
    width: 100%;
    height: 100%;
    min-height: auto;
    min-width: auto;
`;

const PlayIcon = styled(dynamic(() => import('icons/Play.svg?react'), { ssr: false }))`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
`;

const StyledTag = styled(Tag)`
    position: absolute;
    top: ${Spacing16};
    left: ${Spacing16};
`;

const Duration = styled(Typography.Body)`
    position: absolute;
    color: ${ColorNeutralWhite};
    bottom: ${Spacing16};
    right: ${Spacing16};
`;

const GradiantOverlay = styled.div`
    position: absolute;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, rgba(84, 84, 84, 0.20) 34.5%, rgba(0, 0, 0, 0.30) 62%);
    width: 100%;
    height: 100%;
`;

/* #endregion */

const openVideoModal = async (url: string, platform: IProductVideo['platform']) => {
    const { default: videoModal } = await import(/* webpackChunkName: "videoModal" */ 'modules/modal/videoModal');

    const modal = videoModal();

    modal({
        url,
        platform
    });
};

export default function HorizontalVideoCard({
    videoModel: { id, platform },
    ...restProps
}: {
    videoModel: IProductVideo;
}) {
    const video: VideoDetailsProps = { id, platform, subtitles: true };
    const videoDetails = useVideoDetails(video);
    const { t } = useTranslation();

    // Loading..
    if (!videoDetails)
        return (
            <ButtonContainer $loading={!videoDetails}>
                <StyledLoading />
            </ButtonContainer>
        );

    const { duration, videoUrl, thumbnail } = videoDetails;

    return (
        <Card {...restProps}>
            <ButtonContainer
                onClick={() => {
                    GA4Tracking({
                        eventName: 'open_modal',
                        context: 'product_page',
                        category: 'advisory',
                        eventParams: [{ param_name: 'modal_type', param_value: 'video' }]
                    });
                    openVideoModal(videoUrl, platform);
                }}
            >
                <StyledImg alt="thumbnail" src={thumbnail} />
                <GradiantOverlay />
                <StyledTag text={t(HowTo)} />
                <PlayIcon />
                <Duration forwardedAs="span">{duration}</Duration>
            </ButtonContainer>
        </Card>
    );
}
